import './style.scss';
import { gsap } from "gsap";
import { ScrollTrigger, Draggable, InertiaPlugin } from "gsap/all";

gsap.registerPlugin(ScrollTrigger, Draggable, InertiaPlugin);

/**
 * PRO HELPER: Creates a controller for a seamless infinite loop.
 * It calculates the "seam" based on the first clone's position.
 */
function createColumnController(target, speed, reverse = false) {
  const items = Array.from(target.children);
  if (items.length === 0) return null;

  // The 'seam' is the exact offset of the first cloned element.
  // This ensures mathematical perfection even with odd card widths or gaps.
  const originalCount = items.length / 2;
  const firstClone = items[originalCount];
  
  if (!firstClone) return null;

  const loopWidth = firstClone.offsetLeft; 
  const setter = gsap.quickSetter(target, "x", "px");
  const wrap = gsap.utils.wrap(-loopWidth, 0);

  return {
    update: (delta, manualDelta = 0) => {
      const currentX = gsap.getProperty(target, "x");
      const movement = manualDelta || (speed * delta * (reverse ? -1 : 1));
      setter(wrap(currentX + movement));
    },
    width: loopWidth
  };
}

export function your_specialists_block() {
  const block = document.querySelector('.your_specialists_block');
  if (!block) return;

  const wrapper = block.querySelector('.specialists-grid-wrapper');
  const rightColumn = block.querySelector('.right-column');
  const leftColumn = block.querySelector('.left-column');
  if (!wrapper || !rightColumn || !leftColumn) return;

  const mm = gsap.matchMedia();

  // ---------------------------------------------
  // DESKTOP: Vertical Sync (1024px+)
  // ---------------------------------------------
  mm.add("(min-width: 1024px)", () => {
    const getVerticalDistances = () => {
      const h = wrapper.clientHeight;
      return {
        right: Math.max(0, rightColumn.scrollHeight - h),
        left: Math.max(0, leftColumn.scrollHeight - h),
      };
    };

    let distances = getVerticalDistances();

    // Optimize performance
    gsap.set([leftColumn, rightColumn], { willChange: "transform", force3D: true });

    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: wrapper,
        pin: block,
        start: "top top",
        end: () => `+=${Math.max(distances.right, distances.left)}`,
        scrub: 1,
        invalidateOnRefresh: true,
        onRefreshInit: () => { distances = getVerticalDistances(); }
      }
    });

    // Right Column: Scrolls down
    tl.to(rightColumn, { y: () => -distances.right, ease: "none" }, 0);
    // Left Column: Scrolls up (starts from the bottom)
    tl.fromTo(leftColumn, { y: () => -distances.left }, { y: 0, ease: "none" }, 0);

    return () => {
      tl.kill();
      gsap.set([leftColumn, rightColumn], { clearProps: "all" });
    };
  });

  // ---------------------------------------------
  // MOBILE: Horizontal Seamless Loop (< 1024px)
  // ---------------------------------------------
  mm.add("(max-width: 1023px)", () => {
    // 1. Cleanup & Clone
    // Remove any existing clones (vital if resizing window)
    block.querySelectorAll('.is-clone').forEach(el => el.remove());

    [leftColumn, rightColumn].forEach(col => {
      const originalItems = Array.from(col.children);
      originalItems.forEach(item => {
        const clone = item.cloneNode(true);
        clone.classList.add('is-clone');
        col.appendChild(clone);
      });
    });

    const autoSpeed = -1.2;
    let isDragging = false;
    let lastTime = gsap.ticker.time;
    let leftCtrl, rightCtrl;

    // 2. Initialize Logic after DOM paint
    // This ensures offsetLeft measurements are 100% accurate
    requestAnimationFrame(() => {
      leftCtrl = createColumnController(leftColumn, autoSpeed, false);
      rightCtrl = createColumnController(rightColumn, autoSpeed, true);
    });

    const tick = (time) => {
      if (isDragging || !leftCtrl || !rightCtrl) {
        lastTime = time;
        return;
      }
      const delta = (time - lastTime) * 60; // Smooth 60fps normalization
      lastTime = time;
      
      leftCtrl.update(delta);
      rightCtrl.update(delta);
    };

    gsap.ticker.add(tick);

    // 3. Visibility Observer (Battery Saver)
    const observer = new IntersectionObserver(([entry]) => {
      entry.isIntersecting ? gsap.ticker.add(tick) : gsap.ticker.remove(tick);
    }, { threshold: 0.1 });
    observer.observe(block);

    // 4. Draggable Proxy
    const drag = Draggable.create(document.createElement("div"), {
      trigger: wrapper,
      type: "x",
      inertia: true,
      onPress() { 
        isDragging = true; 
      },
      onDrag() {
        if (!leftCtrl || !rightCtrl) return;
        leftCtrl.update(0, this.deltaX);
        rightCtrl.update(0, -this.deltaX); // Moves opposite
      },
      onThrowUpdate() {
        if (!leftCtrl || !rightCtrl) return;
        leftCtrl.update(0, this.deltaX);
        rightCtrl.update(0, -this.deltaX);
      },
      onRelease() { 
        if (!this.isThrowing) {
            isDragging = false;
            lastTime = gsap.ticker.time;
        }
      },
      onThrowComplete() { 
        isDragging = false; 
        lastTime = gsap.ticker.time;
      }
    })[0];

    // 5. Cleanup
    return () => {
      observer.disconnect();
      gsap.ticker.remove(tick);
      drag.kill();
      block.querySelectorAll('.is-clone').forEach(n => n.remove());
      gsap.set([leftColumn, rightColumn], { clearProps: "all" });
    };
  });
}